import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast, Toaster } from 'sonner';
import { Upload, Trash2, Copy, Image as ImageIcon, X, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminImagesPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const getToken = () => localStorage.getItem('admin_token');

  const loadImages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/images`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data);
      }
    } catch (e) {
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadImages(); }, [loadImages]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail || 'Upload failed');
        }

        return await res.json();
      } catch (e) {
        toast.error(`Failed to upload ${file.name}: ${e.message}`);
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    const successCount = results.filter(r => r !== null).length;
    
    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully`);
      loadImages();
    }
    setUploading(false);
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      const res = await fetch(`${API_URL}/api/cms/images/${imageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      if (res.ok) {
        toast.success('Image deleted');
        loadImages();
      } else {
        toast.error('Failed to delete image');
      }
    } catch (e) {
      toast.error('Failed to delete image');
    }
  };

  const copyUrl = (filename) => {
    const url = `${API_URL}/api/uploads/${filename}`;
    navigator.clipboard.writeText(url);
    setCopiedId(filename);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return <div className="text-text-secondary">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Image Library</h1>
          <p className="text-text-secondary mt-1">Upload and manage images for your content</p>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="border-border-line">
        <CardContent className="p-6">
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive 
                ? 'border-tech-blue bg-tech-blue/5' 
                : 'border-border-line hover:border-tech-blue/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
              disabled={uploading}
            />
            
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="text-tech-blue animate-spin mb-4" size={48} />
                <p className="text-text-secondary">Uploading...</p>
              </div>
            ) : (
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-tech-blue/10 flex items-center justify-center mb-4">
                    <Upload className="text-tech-blue" size={32} />
                  </div>
                  <p className="text-text-primary font-medium mb-1">
                    Drop images here or click to upload
                  </p>
                  <p className="text-text-secondary text-sm">
                    PNG, JPG, GIF, WebP, SVG up to 10MB
                  </p>
                </div>
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Images Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {images.map((image) => (
          <Card key={image.id} className="border-border-line overflow-hidden group">
            <div className="aspect-square relative bg-page-bg">
              <img
                src={`${API_URL}/api/uploads/${image.filename}`}
                alt={image.original_name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => copyUrl(image.filename)}
                  className="bg-white/90 hover:bg-white"
                >
                  {copiedId === image.filename ? (
                    <Check size={16} className="text-green-600" />
                  ) : (
                    <Copy size={16} />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleDelete(image.id)}
                  className="bg-white/90 hover:bg-white text-red-500 hover:text-red-600"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
            <CardContent className="p-3">
              <p className="text-xs text-text-primary truncate" title={image.original_name}>
                {image.original_name}
              </p>
              <p className="text-xs text-text-secondary">
                {formatFileSize(image.size)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-12">
          <ImageIcon className="mx-auto text-text-secondary mb-4" size={48} />
          <p className="text-text-secondary">No images uploaded yet</p>
          <p className="text-sm text-text-secondary mt-1">
            Upload images to use in your content
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminImagesPage;
